import { z } from 'zod'

export enum signs_symptom_category {
  GESTANTE = 'GESTANTE',
  LACTANTE = 'LACTANTE',
  TENTANTE = 'TENTANTE',
  CHILDREN = 'CHILDREN',
  WOMAN = 'WOMAN',
  MAN = 'MAN'
}

export const formatted_signs_symptom_category: Record<signs_symptom_category, string> = {
  [signs_symptom_category.GESTANTE]: 'Gestante',
  [signs_symptom_category.LACTANTE]: 'Lactante',
  [signs_symptom_category.TENTANTE]: 'Tentante',
  [signs_symptom_category.CHILDREN]: 'Criança',
  [signs_symptom_category.WOMAN]: 'Mulher',
  [signs_symptom_category.MAN]: 'Homem'
}

export enum signs_symptom_children_range {
  R_0_6M = 'R_0_6M',
  R_6M_2A = 'R_6M_2A',
  R_2_10A = 'R_2_10A',
  R_10_18A = 'R_10_18A'
}

export const formatted_signs_symptom_children_range: Record<signs_symptom_children_range, string> = {
  [signs_symptom_children_range.R_0_6M]: '0 a 6 meses',
  [signs_symptom_children_range.R_6M_2A]: '6 meses a 2 anos',
  [signs_symptom_children_range.R_2_10A]: '2 a 10 anos',
  [signs_symptom_children_range.R_10_18A]: '10 a 18 anos'
}

export enum signs_symptom_trimester {
  TRIM1 = 'TRIM1',
  TRIM2 = 'TRIM2',
  TRIM3 = 'TRIM3'
}

export const formatted_signs_symptom_trimester: Record<signs_symptom_trimester, string> = {
  [signs_symptom_trimester.TRIM1]: '1º Trimestre',
  [signs_symptom_trimester.TRIM2]: '2º Trimestre',
  [signs_symptom_trimester.TRIM3]: '3º Trimestre'
}

export enum signs_symptom_status {
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  DELETED = 'DELETED'
}

export const formatted_signs_symptom_status: Record<signs_symptom_status, string> = {
  [signs_symptom_status.DRAFT]: 'Rascunho',
  [signs_symptom_status.PROCESSING]: 'Processando',
  [signs_symptom_status.ACTIVE]: 'Concluída',
  [signs_symptom_status.FAILED]: 'Falhou',
  [signs_symptom_status.DELETED]: 'Deletado'
}

/**
 * Mapa { [symptomId]: true } com sintomas marcados pela paciente.
 * Os sintomas são definidos em `frontend/src/components/signs-symptoms/data.ts`.
 */
export type SymptomChecks = Record<string, boolean>

/**
 * Sintomas gestacionais por trimestre — aplicável APENAS a categoria GESTANTE.
 * Outras categorias usam só `generalSymptoms`.
 */
export type TrimesterChecks = {
  [signs_symptom_trimester.TRIM1]: SymptomChecks
  [signs_symptom_trimester.TRIM2]: SymptomChecks
  [signs_symptom_trimester.TRIM3]: SymptomChecks
}

export const emptyTrimesterChecks = (): TrimesterChecks => ({
  [signs_symptom_trimester.TRIM1]: {},
  [signs_symptom_trimester.TRIM2]: {},
  [signs_symptom_trimester.TRIM3]: {}
})

/**
 * Registro persistido. Espelha o model `signs_symptoms` do Prisma + a estrutura
 * da resposta da IA armazenada em `response`/`contentHtml`.
 */
export interface SignsSymptomProps {
  id?: number
  ref: string
  userId?: number
  patientName: string
  category: signs_symptom_category
  weeksOfGestation?: number | null
  childrenRange?: signs_symptom_children_range | null
  generalSymptoms: SymptomChecks
  /** Sintomas gestacionais por trimestre (apenas categoria GESTANTE). */
  gestationalSymptoms?: TrimesterChecks | null
  title?: string | null
  summary?: string | null
  /** JSON parsed da IA (string) — content_html pode estar dentro */
  response?: string | null
  /** HTML pronto pra render */
  contentHtml?: string | null
  spentValue?: number | null
  status: signs_symptom_status
  createdAt: string
  updatedAt?: string | null
}

/**
 * Schema do form de identificação da paciente.
 */
export const signsSymptomFormSchema = z.object({
  patientName: z.string().min(2, 'Nome inválido.').max(255, 'Limite 255 caracteres.'),
  category: z.nativeEnum(signs_symptom_category, { errorMap: () => ({ message: 'Categoria inválida.' }) }),
  weeksOfGestation: z.coerce.number().int().min(1, 'Mínimo 1 semana.').max(42, 'Máximo 42 semanas.').optional().nullable(),
  childrenRange: z.nativeEnum(signs_symptom_children_range).optional().nullable()
})

export type SignsSymptomFormSchema = z.infer<typeof signsSymptomFormSchema>

/**
 * Validação cruzada chamada antes do submit (campos condicionais).
 * Retorna mensagem do primeiro erro ou null.
 */
export function validateSignsSymptomCrossFields(values: SignsSymptomFormSchema): string | null {
  if (values.category === signs_symptom_category.GESTANTE && !values.weeksOfGestation) return 'Informe as semanas de gestação.'
  if (values.category === signs_symptom_category.CHILDREN && !values.childrenRange) return 'Informe a faixa etária.'
  return null
}

export const countCheckedSymptoms = (record: Pick<SignsSymptomProps, 'generalSymptoms' | 'gestationalSymptoms'>): number => {
  const general = Object.values(record.generalSymptoms || {}).filter(Boolean).length
  const gestational = record.gestationalSymptoms
    ? Object.values(record.gestationalSymptoms).reduce((acc, t) => acc + Object.values(t).filter(Boolean).length, 0)
    : 0
  return general + gestational
}

/**
 * Determina o trimestre sugerido a partir das semanas de gestação.
 * Convenção Mãe Nutrida: 1-14 → TRIM1; 15-27 → TRIM2; 28+ → TRIM3.
 */
export const guessActiveTrimester = (weeks?: number | null): signs_symptom_trimester => {
  if (!weeks || weeks <= 0) return signs_symptom_trimester.TRIM1
  if (weeks <= 14) return signs_symptom_trimester.TRIM1
  if (weeks <= 27) return signs_symptom_trimester.TRIM2
  return signs_symptom_trimester.TRIM3
}

